import React, {useEffect, useState} from "react";
import {Outlet} from "react-router-dom";
import {
    AppBar,
    Avatar,
    Badge,
    Box,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Stack,
    Toolbar,
    Tooltip,
    Link as MuiLink,
    Typography,
} from "@mui/material";
import {getMe, renew} from "../service/auth.service";
import {useAuthStore} from "../store/auth.store";
import {Link, useNavigate} from "react-router";
import {ModalWindow} from "./modal/ModalWindow";
import {LoginForm} from "./auth/LoginForm";
import {PasswordRecoveryForm} from "./auth/PasswordRecoveryForm";
import {RegisterForm} from "./auth/RegisterForm";
import {enqueueSnackbar} from "notistack";
import MenuIcon from '@mui/icons-material/Menu';
import AccountCircleOutlinedIcon from '@mui/icons-material/AccountCircleOutlined';
import GroupsOutlinedIcon from '@mui/icons-material/GroupsOutlined';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import LoginOutlinedIcon from '@mui/icons-material/LoginOutlined';
import RealEstateAgentOutlinedIcon from '@mui/icons-material/RealEstateAgentOutlined';
import ContactPhoneOutlinedIcon from '@mui/icons-material/ContactPhoneOutlined';
import {User} from "../types/User";
import {parseQueryParams, removeQueryParam} from "../utils/UrlUtils";
import ContactSupportOutlinedIcon from "@mui/icons-material/ContactSupportOutlined";
import AddHomeWorkOutlinedIcon from '@mui/icons-material/AddHomeWorkOutlined';
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import ScheduleOutlinedIcon from '@mui/icons-material/ScheduleOutlined';
import {SubscriptionModal} from "./proposal/SubscriptionModal";
import {keepPreviousData, useQuery} from "@tanstack/react-query";
import {requireAttentionAsAdmin, requireAttentionAsUser} from "../service/ticket.service";

const Layout: React.FC = () => {
    const authStore = useAuthStore();
    const navigate = useNavigate();
    const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
    const open = Boolean(anchorEl);
    const canManageUsers = authStore.can("manage_users");
    const havePremium = authStore.can("see_premium");
    const hasFullAccess = authStore.can("full_access");
    const [showSubscribe, setShowSubscribe] = useState(false);

    const [loginFormLoading, setLoginFormLoading] = useState<boolean>(false);
    const [registerFormLoading, setRegisterFormLoading] = useState<boolean>(false);
    const [passwordRecoveryFormLoading, setPasswordRecoveryFormLoading] = useState<boolean>(false);

    useEffect(() => {
        const interval = setInterval(() => {
            if (authStore.jwt) {
                renew().then(response => {
                    authStore.setJwt(response.data.jwt)
                }).catch((e) => {
                    authStore.logout(navigate);
                })
            }
        }, 7 * 60 * 1000);

        return () => {
            clearInterval(interval)
        }
    });


    const {data: containNotifications} = useQuery({
        queryKey: ['notifications', 'support'],
        refetchOnWindowFocus: false,
        enabled: !!authStore.user,
        queryFn: () => {
            let promise = authStore.can('be_support')
                ? requireAttentionAsAdmin()
                : requireAttentionAsUser();
            return promise.then(response => {
                return response.status === 200;
            }).catch(error => {
                enqueueSnackbar({
                    message: "Failed to load support tickets notifications: " + error.message,
                    variant: "error"
                })
                return false;
            })
        },
        placeholderData: keepPreviousData
    });

    useEffect(() => {
        if (!authStore.user && authStore.jwt) {
            getMe().then(r => {
                if (r.data) {
                    authStore.setUser(r.data)
                } else {
                    authStore.setUser(undefined);
                    authStore.setJwt(undefined);
                }
            }).catch((e) => {
                enqueueSnackbar({variant: "error", message: "Failed to load current user details, " + e.message})
            });
        }
    }, [authStore, authStore.user, authStore.jwt]);


    useEffect(() => {
        if (authStore.jwt) {
            return
        }
        if (authStore.showLogin) {
            return
        }
        const params = parseQueryParams(document.location.toString())
        if (params.login !== undefined) {
            removeQueryParam("login");
            authStore.setShowLogin(true);
        }
    }, [authStore, authStore.user, authStore.jwt]);

    function prepareUserName(user: User) {
        let name:string;
        if (user.username) {
            name = user.username
        } else if (user.login) {
            const position = user.login.indexOf("@");
            name = position > -1 ? user.login.substring(0, position) : user.login;
        } else if(user.phone) {
            name = user.phone;
        } else {
            return null;
        }
        return <MuiLink href={"/profile"} onClick={(e) => {
            e.preventDefault();
            navigate("/profile")
        }}>{name}</MuiLink>
    }
    return (<Box sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
    }}>
        <AppBar position="static" color={"default"}>
            <Toolbar>
                <a href="/#" onClick={(e) => {
                    e.preventDefault();
                    let isInIframe;
                    try {
                        isInIframe = window.self !== window.top;
                    } catch (e) {
                        isInIframe = true;
                    }
                    if (isInIframe) {
                        window.open("https://goodplacegeorgia.com/", '_blank')
                    } else {
                        navigate("/")
                    }
                }}>
                    <Avatar
                        alt="Logo"
                        style={{margin: '-64px 0'}}
                        src="/images/logo.png"
                        sx={{width: 128, height: 128}}
                        variant="rounded" // or "circular" or "square"
                    />
                </a>
                <Typography sx={{display: {xs: 'none', md: 'block'}, flexGrow: 1}}>
                    All ads for sale of secondary real estate in Batumi are collected and verified in one table.
                </Typography>

                <Stack flexDirection={"row"} gap={"10px"} alignItems={"center"}>
                    {authStore.user ? prepareUserName(authStore.user) : null}

                    {!authStore.jwt && <IconButton onClick={() => authStore.setShowLogin(true)}>
                        <LoginOutlinedIcon fontSize="medium"/>
                    </IconButton>}


                    {authStore.jwt && <Tooltip title="More options">

                            <IconButton
                                id="topbar-menu-icon-button"
                                edge="end"
                                aria-label="menu"
                                aria-controls={open ? "topbar-menu" : undefined}
                                aria-haspopup="true"
                                aria-expanded={open ? "true" : undefined}
                                onClick={(e) => setAnchorEl(e.currentTarget)}
                                color="inherit"
                                sx={{ml: 1}}
                            >
                                <Badge
                                    variant="dot"
                                    color="error"
                                    overlap="circular"
                                    invisible={!containNotifications}
                                    anchorOrigin={{
                                        vertical: "top",
                                        horizontal: "right",
                                    }}
                                >
                                    <MenuIcon fontSize={"large"}/>
                                </Badge>
                            </IconButton>
                    </Tooltip>}
                </Stack>

                <Menu
                    anchorEl={anchorEl}
                    open={open}
                    onClose={() => setAnchorEl(null)}
                    onClick={() => setAnchorEl(null)}
                    anchorOrigin={{horizontal: "right", vertical: "bottom"}}
                >
                    <MenuItem onClick={() => navigate("/")}>
                        <ListItemIcon>
                            <RealEstateAgentOutlinedIcon fontSize="medium"/>
                        </ListItemIcon>
                        Home
                    </MenuItem>
                    <MenuItem onClick={() => {
                        navigate("/prop/new")
                    }}>
                        <ListItemIcon>
                            <AddHomeWorkOutlinedIcon/>
                        </ListItemIcon>
                        Add Listing
                    </MenuItem>
                    <MenuItem onClick={() => {
                        authStore.jwt
                            ? navigate("/profile")
                            : authStore.setShowLogin(true)
                    }}>
                        <ListItemIcon>
                            <AccountCircleOutlinedIcon fontSize="medium"/>
                        </ListItemIcon>
                        Profile
                    </MenuItem>
                    {canManageUsers && <MenuItem onClick={() => navigate("/users")}>
                        <ListItemIcon>
                            <GroupsOutlinedIcon fontSize="medium"/>
                        </ListItemIcon>
                        Users
                    </MenuItem>}

                    {canManageUsers && <MenuItem onClick={() => navigate("/quick-logins")}>
                        <ListItemIcon>
                            <ContactPhoneOutlinedIcon fontSize="medium"/>
                        </ListItemIcon>
                        Phone-Accounts
                    </MenuItem>}
                    {authStore.jwt && <MenuItem onClick={() => navigate("/support")}>

                        <ListItemIcon>
                            <ContactSupportOutlinedIcon fontSize="medium"/>
                        </ListItemIcon>
                        <Badge
                            variant="dot"
                            color="error"
                            overlap="rectangular"
                            invisible={!containNotifications}
                            anchorOrigin={{
                                vertical: "top",
                                horizontal: "right",
                            }}
                        >
                            Support Tickets
                        </Badge>

                    </MenuItem>}

                    {havePremium && <MenuItem onClick={() => setShowSubscribe(true)}>
                        <ListItemIcon>
                            <NotificationsActiveOutlinedIcon fontSize="medium"/>
                        </ListItemIcon>
                        Daily Subscription
                    </MenuItem>}

                    {hasFullAccess && <MenuItem onClick={() => navigate("/scheduler")}>
                        <ListItemIcon>
                            <ScheduleOutlinedIcon fontSize="medium"/>
                        </ListItemIcon>
                        Scheduler
                    </MenuItem>}

                    {authStore.jwt && <MenuItem onClick={() => authStore.logout(navigate)}>
                        <ListItemIcon>
                            <LogoutOutlinedIcon fontSize="medium"/>
                        </ListItemIcon>
                        Logout
                    </MenuItem>}
                    {!authStore.jwt && <MenuItem onClick={() => authStore.setShowLogin(true)}>
                        <ListItemIcon>
                            <LoginOutlinedIcon fontSize="medium"/>
                        </ListItemIcon>
                        Login
                    </MenuItem>}
                </Menu>

            </Toolbar>
        </AppBar>
        <Box component="main" sx={{
                 flexGrow: 1,
                 padding: { xs: '10px', md: '26px' },
             }}>
            <Outlet/>
        </Box>
        <Box
            component="footer"
            sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                py: 2,
                px: { xs: 2, md: 4 },
                backgroundColor: 'background.paper',
            }}
        >
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                justifyContent="space-between"
                alignItems={{ xs: 'flex-start', sm: 'center' }}
            >
                <Typography variant="body2" color="text.secondary">
                    © {new Date().getFullYear()} GoodPlace Georgia
                </Typography>

                <Stack direction="row" spacing={2}>
                    <MuiLink href="/terms-and-conditions" target={"_blank"}  rel="noopener noreferrer">Terms & Conditions</MuiLink>
                    <MuiLink href="/privacy-policy" target={"_blank"}  rel="noopener noreferrer">Privacy Policy</MuiLink>
                </Stack>
            </Stack>
        </Box>
        {authStore.showLogin && <ModalWindow loading={loginFormLoading} onClose={() => authStore.setShowLogin(false)}>
            <LoginForm setLoading={setLoginFormLoading}/>
        </ModalWindow>}
        {authStore.showRegister &&
            <ModalWindow loading={registerFormLoading} onClose={() => authStore.setShowRegister(false)}>
                <RegisterForm setLoading={setRegisterFormLoading}/>
            </ModalWindow>}
        {authStore.showResetPassword &&
            <ModalWindow loading={passwordRecoveryFormLoading} onClose={() => authStore.setShowResetPassword(false)}>
                <PasswordRecoveryForm setLoading={setPasswordRecoveryFormLoading}/>
            </ModalWindow>}

        {showSubscribe && <SubscriptionModal
            onClose={() => setShowSubscribe(false)}
        />}
    </Box>);
};

export default Layout;
